/**
 * member-directory (module) — client-side filter / sort for the member directory,
 * plus the member-detail modal populate. Ported from designs/directory.html.
 *
 * Filter: search (name + organization), Expertise radio, Working-group radio,
 * Organization select. Sort: last name A→Z / Z→A, or organization then last name;
 * name sorts get surname letter dividers. Keeps the live count + empty state in
 * sync. Clear resets every control. The mobile toggle opens the collapsed filters.
 *
 * Modal: clicking a card (or Enter/Space on a focused card) copies that card's
 * data into the shared #member-modal and clicks #mm-open — which the theme's
 * modal controller (components/modal.js, bound to [data-fr-modal-open]) opens.
 * NOTE: unlike the mockup, the bio is read from the card's `data-bio` attribute
 * (no synthesised bio); an empty/absent bio renders as "—".
 *
 * Vanilla + existence-guarded; mirrors blocks/card-grid-with-filters.js and
 * components/modal.js.
 */
( function ( _s ) {
	_s( document ).ready( function () {
		const form = document.querySelector( '.dir-sidebar__form' );
		if ( ! form ) {
			return;
		}

		const search = document.getElementById( 'filter-search' );
		const clearBtn = document.getElementById( 'filter-clear' );
		const sortSel = document.getElementById( 'filter-sort' );
		const orgSel = document.getElementById( 'filter-org' );
		const grid = document.querySelector( '.dir-grid' );
		const members = Array.from( document.querySelectorAll( '.member-card' ) );
		const count = document.getElementById( 'filter-count' );
		const empty = document.getElementById( 'dir-empty' );

		const getRadio = ( name ) => {
			const el = form.querySelector( `input[name="${ name }"]:checked` );
			return el ? el.value : '';
		};
		const setRadio = ( name, value ) => {
			const el = form.querySelector( `input[name="${ name }"][value="${ value }"]` );
			if ( el ) {
				el.checked = true;
			}
		};
		const lastOf = ( m ) => ( m.dataset.last || '' ).toLowerCase();
		const orgOf = ( m ) => ( m.dataset.org || '' ).toLowerCase();

		function apply() {
			const f = {
				exp: getRadio( 'focus' ),
				wg: getRadio( 'wg' ),
				org: ( orgSel && orgSel.value ) || '',
				sort: ( sortSel && sortSel.value ) || 'az',
				search: ( ( search && search.value ) || '' ).trim().toLowerCase(),
			};
			let visible = 0;
			members.forEach( ( m ) => {
				const show =
					( ! f.exp || m.dataset.focus === f.exp ) &&
					( ! f.wg || m.dataset.wg === f.wg ) &&
					( ! f.org || m.dataset.org === f.org ) &&
					( ! f.search || m.textContent.toLowerCase().includes( f.search ) );
				m.hidden = ! show;
				if ( show ) {
					visible++;
				}
			} );

			const sorter = ( a, b ) => {
				if ( f.sort === 'az' ) {
					return lastOf( a ).localeCompare( lastOf( b ) );
				}
				if ( f.sort === 'za' ) {
					return lastOf( b ).localeCompare( lastOf( a ) );
				}
				if ( f.sort === 'org' ) {
					return orgOf( a ).localeCompare( orgOf( b ) ) || lastOf( a ).localeCompare( lastOf( b ) );
				}
				return 0;
			};

			if ( grid ) {
				grid.querySelectorAll( '.dir-letter-head' ).forEach( ( h ) => h.remove() );
				const sorted = members.slice().sort( sorter );
				const nameSort = f.sort === 'az' || f.sort === 'za';
				if ( nameSort ) {
					const counts = {};
					sorted.forEach( ( m ) => {
						if ( ! m.hidden ) {
							const L = ( lastOf( m ).charAt( 0 ) || '#' ).toUpperCase();
							counts[ L ] = ( counts[ L ] || 0 ) + 1;
						}
					} );
					let cur = null;
					sorted.forEach( ( m ) => {
						if ( ! m.hidden ) {
							const L = ( lastOf( m ).charAt( 0 ) || '#' ).toUpperCase();
							if ( L !== cur ) {
								cur = L;
								const n = counts[ L ];
								const head = document.createElement( 'div' );
								head.className = 'dir-letter-head';
								head.innerHTML = `<h2 class="dir-letter-head__title">${ L }</h2><span class="dir-letter-head__count">${ n } member${ n === 1 ? '' : 's' }</span>`;
								grid.appendChild( head );
							}
						}
						grid.appendChild( m );
					} );
				} else {
					sorted.forEach( ( m ) => grid.appendChild( m ) );
				}
			}

			if ( count ) {
				count.textContent = `${ visible } of ${ members.length } members`;
			}
			if ( empty ) {
				empty.classList.toggle( 'is-visible', visible === 0 );
			}
		}

		form.addEventListener( 'change', apply );
		if ( search ) {
			search.addEventListener( 'input', apply );
		}
		if ( clearBtn ) {
			clearBtn.addEventListener( 'click', () => {
				setRadio( 'focus', '' );
				setRadio( 'wg', '' );
				if ( orgSel ) {
					orgSel.value = '';
				}
				if ( sortSel ) {
					sortSel.value = 'az';
				}
				if ( search ) {
					search.value = '';
				}
				apply();
			} );
		}

		const toggleBtn = document.getElementById( 'filter-toggle' );
		if ( toggleBtn ) {
			toggleBtn.addEventListener( 'click', () => {
				const open = form.classList.toggle( 'filters-open' );
				toggleBtn.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
			} );
		}

		apply();

		// ── MEMBER DETAIL MODAL ────────────────────────────────────────────────
		const set = ( id, html ) => {
			const el = document.getElementById( id );
			if ( el ) {
				el.innerHTML = html;
			}
		};
		const setText = ( id, text ) => {
			const el = document.getElementById( id );
			if ( el ) {
				el.textContent = text;
			}
		};
		const esc = ( s ) =>
			s.replace( /[&<>"']/g, ( c ) => ( { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ c ] ) );
		const WG = {
			'policy-tracking': 'Policy Tracking',
			'data-use': 'Data Use',
			'dei-climate': 'DEI Climate',
			'student-support': 'Student Support',
			communications: 'Communications',
		};

		function populate( m ) {
			const avatar = m.querySelector( '.member-card__avatar' );
			const name = ( m.querySelector( '.member-card__name' )?.textContent || '' ).trim();
			const meta = ( m.querySelector( '.member-card__meta' )?.textContent || '' ).trim();
			const emailEl = m.querySelector( '.member-card__email' );
			const wgLabel = WG[ m.dataset.wg ] || '';
			const mmAvatar = document.getElementById( 'mm-avatar' );

			if ( avatar && mmAvatar ) {
				mmAvatar.innerHTML = avatar.innerHTML;
			}
			setText( 'mm-name', name );
			setText( 'mm-meta', meta );

			// The card's tag chips already carry expertise + working group, so mirror
			// them verbatim — no separate WG chip (that would duplicate it; the mockup
			// prepended WG only because its card tags didn't include the WG).
			const tags = [];
			m.querySelectorAll( '.member-card__tags .member-tag' ).forEach( ( t ) => tags.push( t.outerHTML ) );
			set( 'mm-tags', tags.join( '' ) );

			// Bio from the card's data-bio (no synthesised bio); em dash when empty.
			const bio = ( m.dataset.bio || '' ).trim();
			setText( 'mm-bio', bio || '—' );

			set(
				'mm-email',
				emailEl
					? `<a href="${ emailEl.getAttribute( 'href' ) }">${ esc( emailEl.textContent.trim() ) }</a>`
					: 'Not shared'
			);
			setText( 'mm-wg', wgLabel || '—' );
		}

		// ── COMMENTS (DIR-9) ───────────────────────────────────────────────────
		// The comments container is present only for members AND only while the Theme
		// Settings "Comments on member profiles" switch is on (the server gates both), so
		// everything here is a no-op for a logged-out/affiliate view, and for everyone
		// while profile commenting is switched off. On modal open we
		// fetch that member's thread (the shared ArticleComments partial, rendered
		// server-side against their hidden member_profile post) and inject it. A stamped
		// token guards against a slow response for member A landing after B was opened.
		const commentsBox = document.getElementById( 'mm-comments' );
		let commentsToken = 0;

		function loadComments( userId ) {
			if ( ! commentsBox || ! userId ) {
				return;
			}

			const url = commentsBox.dataset.ajaxUrl;
			const action = commentsBox.dataset.action;
			if ( ! url || ! action ) {
				return;
			}

			const token = ++commentsToken;
			commentsBox.innerHTML = '<p class="member-modal__comments-loading">Loading comments…</p>';

			const endpoint =
				url +
				( url.indexOf( '?' ) === -1 ? '?' : '&' ) +
				'action=' +
				encodeURIComponent( action ) +
				'&member=' +
				encodeURIComponent( userId );

			fetch( endpoint, { credentials: 'same-origin' } )
				.then( ( r ) => r.json() )
				.then( ( res ) => {
					if ( token !== commentsToken ) {
						return; // a newer open superseded this fetch
					}
					if ( res && res.success && res.data && res.data.html ) {
						commentsBox.innerHTML = res.data.html;
					} else {
						commentsBox.innerHTML =
							'<p class="member-modal__comments-loading">Comments couldn’t be loaded.</p>';
					}
				} )
				.catch( () => {
					if ( token === commentsToken ) {
						commentsBox.innerHTML =
							'<p class="member-modal__comments-loading">Comments couldn’t be loaded.</p>';
					}
				} );
		}

		const openMemberModal = ( m ) => {
			populate( m );
			loadComments( m.dataset.user );
			const opener = document.getElementById( 'mm-open' );
			if ( opener ) {
				opener.click();
			}
		};

		members.forEach( ( m ) => {
			m.addEventListener( 'click', ( e ) => {
				if ( e.target.closest( 'a' ) ) {
					return;
				}
				openMemberModal( m );
			} );
			m.setAttribute( 'tabindex', '0' );
			m.addEventListener( 'keydown', ( e ) => {
				if ( ( e.key === 'Enter' || e.key === ' ' ) && e.target === m ) {
					e.preventDefault();
					openMemberModal( m );
				}
			} );
		} );

		// Deep link: /directory/#member-<id> opens that member's modal on load — used by
		// the comment permalink + moderation emails (MemberProfileCommentsProvider
		// rewrites a hidden-post comment link to here), and shareable directly.
		const openFromHash = () => {
			const match = /^#member-(\d+)$/.exec( window.location.hash );
			if ( ! match ) {
				return;
			}
			const card = members.find( ( m ) => m.dataset.user === match[ 1 ] );
			if ( card ) {
				openMemberModal( card );
			}
		};
		openFromHash();
	} );
} )( window._s );
